/**
 * CashFlowForecastWidget — dashboard card that predicts upcoming collections
 * using per-customer payment velocity (not blind due dates).
 *
 * What it shows:
 *   - Hero: predicted collections in the next 30 days (with 60-day rollup)
 *   - 4 horizontal "buckets": Overdue · This week · Next week · Rest of month
 *   - "Up next" list — top 5 invoices ranked by predicted pay date
 *   - Velocity stats: avg days-to-paid · on-time rate · % paid online
 *
 * Why it matters:
 *   CPA firms / bookkeepers manually build this in Excel every Monday morning.
 *   Surfacing it natively is the single biggest reason firms ask their staff
 *   to learn pivot tables. By giving them a real-time, customer-velocity-aware
 *   version for free, we become indispensable.
 *
 * Empty state:
 *   When no outstanding invoices exist OR there's not enough payment history
 *   to forecast confidently, the widget shows a friendly explainer instead
 *   of misleading $0.00s.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  AlertTriangle,
  Calendar,
  Zap,
  ArrowRight,
  Sparkles,
  HelpCircle,
  ChevronDown,
  CalendarRange,
  Loader2,
} from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

// Help icon with a plain-English explainer of how the AI forecast works.
// Lives next to the widget title so users can self-serve "how is this
// number computed?" without us writing a docs page.
const ForecastExplainer = ({ sampleSize = 0 }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded-full"
        data-testid="cashflow-explainer-trigger"
        onClick={(e) => e.stopPropagation()}
      >
        <HelpCircle className="w-3.5 h-3.5" />
        <span className="sr-only">How does the forecast work?</span>
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 text-sm" align="start" data-testid="cashflow-explainer-popover">
      <p className="font-semibold mb-1.5 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
        How the AI forecasts your cash
      </p>
      <p className="text-muted-foreground leading-relaxed mb-2">
        We look at your last 90 days of <strong>paid invoices</strong> ({sampleSize} so far)
        and learn each customer's payment pattern — when they typically pay vs. their invoice
        due date.
      </p>
      <ul className="text-xs text-muted-foreground space-y-1 mb-2 list-disc pl-4">
        <li>Customers with 5+ paid invoices get a <strong>high-confidence</strong> ETA based on their personal payment velocity.</li>
        <li>New customers fall back to your workspace's average days-to-paid.</li>
        <li>Overdue invoices roll into "Overdue" regardless of customer history.</li>
        <li>The 60-day rollup blends inflows (predicted collections) with outflows (upcoming bills).</li>
      </ul>
      <p className="text-[11px] text-muted-foreground italic">
        Want sharper forecasts? Keep marking invoices as paid in CountBucks. More history
        = tighter predictions per customer.
      </p>
    </PopoverContent>
  </Popover>
);

// Right-side hero tile with a horizon dropdown.
// Default = 60 days (matches the original UX). User can pick 7/30/60/90/180/365
// or open a "Custom days" input. Refreshes via the same /cash-flow-forecast
// endpoint with a `?horizon=N` query param — backend re-aggregates predicted
// pay dates server-side rather than the client filtering items locally.
const HORIZON_PRESETS = [
  { days: 7,   label: 'Next 7 days' },
  { days: 30,  label: 'Next 30 days' },
  { days: 60,  label: 'Next 60 days' },
  { days: 90,  label: 'Next 90 days' },
  { days: 180, label: 'Next 6 months' },
  { days: 365, label: 'Next 12 months' },
];

const HorizonHeroTile = ({ data, horizon, refreshing, onChange }) => {
  const [customOpen, setCustomOpen] = useState(false);
  const [customDays, setCustomDays] = useState(String(horizon));
  const preset = HORIZON_PRESETS.find(p => p.days === horizon);
  const label = preset ? preset.label : `Next ${horizon} days`;

  return (
    <div className="rounded-xl bg-blue-500/5 ring-1 ring-blue-500/10 p-4 relative">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-blue-700 dark:text-blue-400 font-semibold">
          {label}
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-0.5 text-[10px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 rounded-md px-1.5 py-0.5 hover:bg-zinc-100 dark:hover:bg-white/5 transition"
              data-testid="cashflow-horizon-trigger"
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Change'}
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {HORIZON_PRESETS.map(p => (
              <DropdownMenuItem
                key={p.days}
                onClick={() => onChange(p.days)}
                data-testid={`cashflow-horizon-${p.days}`}
              >
                {p.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setCustomOpen(true)}
              data-testid="cashflow-horizon-custom"
            >
              <CalendarRange className="w-3.5 h-3.5 mr-1.5" /> Custom days…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="text-2xl sm:text-3xl font-bold tabular-nums mt-1" data-testid="cashflow-horizon-total">
        {fmtCurrency(data.horizon_total ?? data.forecast_next_60_days)}
      </p>
      <p className="text-[11px] text-muted-foreground mt-1">
        {data.horizon_count != null
          ? `${data.horizon_count} invoice${data.horizon_count === 1 ? '' : 's'} predicted`
          : 'running total'}
      </p>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Custom horizon</DialogTitle>
            <DialogDescription>How many days ahead to forecast? (7 – 365)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="number"
              min={7}
              max={365}
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              data-testid="cashflow-horizon-custom-days"
            />
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                const n = parseInt(customDays, 10);
                if (!isNaN(n) && n >= 7 && n <= 365) {
                  onChange(n);
                  setCustomOpen(false);
                }
              }}
              data-testid="cashflow-horizon-custom-apply"
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function CashFlowForecastWidget({ workspaceId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [horizon, setHorizon] = useState(60); // user-selected horizon in days
  const [horizonRefreshing, setHorizonRefreshing] = useState(false);

  const fetchForecast = useCallback(async (h) => {
    if (!workspaceId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/cash-flow-forecast?workspace_id=${workspaceId}&horizon=${h}`);
      if (!r.ok) throw new Error('forecast fetch failed');
      return await r.json();
    } catch {
      return null;
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const j = await fetchForecast(horizon);
      if (cancelled) return;
      if (j) setData(j); else setError(true);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // We deliberately exclude `horizon` here — initial mount only. Horizon
    // changes go through changeHorizon() which does a lightweight refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, fetchForecast]);

  const changeHorizon = async (newHorizon) => {
    setHorizon(newHorizon);
    setHorizonRefreshing(true);
    const j = await fetchForecast(newHorizon);
    if (j) setData(j);
    setHorizonRefreshing(false);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-card p-6 animate-pulse" data-testid="cashflow-forecast-loading">
        <div className="h-5 w-40 bg-muted rounded mb-4" />
        <div className="h-12 w-64 bg-muted rounded mb-6" />
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-20 bg-muted rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (error || !data) return null;

  const buckets = data.buckets || {};
  const velocity = data.velocity || {};
  const hasOutstanding = (data.outstanding_invoice_count || 0) > 0;

  // Empty state — no outstanding invoices
  if (!hasOutstanding) {
    return (
      <div
        className="rounded-2xl border bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-zinc-900 p-6"
        data-testid="cashflow-forecast-empty"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-base flex items-center gap-1.5">
              Cash Flow Forecast
              <ForecastExplainer sampleSize={0} />
            </h3>
            <p className="text-xs text-muted-foreground">Predict your next 60 days of collections</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          You have no outstanding invoices right now — beautifully on top of it. Once you send invoices,
          this widget will predict when each one will actually be paid using your customers' payment history.
        </p>
      </div>
    );
  }

  // Pick the 4 most relevant buckets for the compact view
  const compactBuckets = [
    { key: 'overdue', data: buckets.overdue, color: 'red', icon: AlertTriangle },
    { key: 'this_week', data: buckets.this_week, color: 'emerald', icon: Zap },
    { key: 'next_week', data: buckets.next_week, color: 'blue', icon: Calendar },
    { key: 'rest_of_month', data: buckets.rest_of_month, color: 'zinc', icon: Calendar },
  ];

  return (
    <div
      className="rounded-2xl border bg-card p-5 sm:p-6"
      data-testid="cashflow-forecast-widget"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-base flex items-center gap-1.5">
              Cash Flow Forecast
              <span className="px-1.5 py-0.5 rounded-md bg-gradient-to-r from-purple-500/15 to-blue-500/15 text-[10px] font-semibold text-purple-700 dark:text-purple-300 ring-1 ring-purple-500/20 inline-flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> AI
              </span>
              <ForecastExplainer sampleSize={velocity.paid_invoice_sample_size || 0} />
            </h3>
            <p className="text-xs text-muted-foreground">
              Predicted from {velocity.paid_invoice_sample_size || 0} paid invoices
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/invoices?filter=outstanding')}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="cashflow-view-all"
        >
          View all
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* Hero — fixed 30-day + user-selectable horizon */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl bg-emerald-500/5 ring-1 ring-emerald-500/10 p-4">
          <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold">Next 30 days</p>
          <p className="text-2xl sm:text-3xl font-bold tabular-nums mt-1" data-testid="cashflow-forecast-30">
            {fmtCurrency(data.forecast_next_30_days)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">expected to collect</p>
        </div>
        <HorizonHeroTile
          data={data}
          horizon={horizon}
          refreshing={horizonRefreshing}
          onChange={changeHorizon}
        />
      </div>

      {/* 4-bucket strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {compactBuckets.map(({ key, data: b, color, icon: Icon }) => {
          if (!b) return null;
          const colorClass = {
            red:     'bg-red-500/10 ring-red-500/20 text-red-700 dark:text-red-400',
            emerald: 'bg-emerald-500/10 ring-emerald-500/20 text-emerald-700 dark:text-emerald-400',
            blue:    'bg-blue-500/10 ring-blue-500/20 text-blue-700 dark:text-blue-400',
            zinc:    'bg-zinc-500/10 ring-zinc-500/20 text-zinc-700 dark:text-zinc-300',
          }[color];
          return (
            <div
              key={key}
              className={cn("rounded-lg ring-1 p-3", colorClass)}
              data-testid={`cashflow-bucket-${key}`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5" />
                <p className="text-[10px] font-semibold uppercase tracking-wide">{b.label}</p>
              </div>
              <p className="text-lg font-bold tabular-nums">{fmtCurrency(b.amount)}</p>
              <p className="text-[10px] opacity-70">{b.count} invoice{b.count === 1 ? '' : 's'}</p>
            </div>
          );
        })}
      </div>

      {/* Up next — top 5 predicted collections */}
      {(data.upcoming || []).length > 0 && (
        <div className="space-y-2 mb-5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Up next</p>
          {(data.upcoming || []).slice(0, 5).map((item) => (
            <button
              key={item.invoice_id}
              onClick={() => navigate(`/invoices?preview=${item.invoice_id}`)}
              className="w-full flex items-center justify-between gap-3 py-2 px-3 rounded-lg hover:bg-accent/50 text-left transition-colors"
              data-testid={`cashflow-upcoming-${item.invoice_no}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{item.customer_name}</p>
                <p className="text-xs text-muted-foreground">
                  Invoice #{item.invoice_no} ·{' '}
                  {item.days_until_predicted_pay === 0
                    ? 'today'
                    : item.days_until_predicted_pay === 1
                    ? 'tomorrow'
                    : `in ${item.days_until_predicted_pay} days`}
                  {item.confidence === 'high' && (
                    <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" title="High confidence — 5+ paid invoices from this customer" />
                  )}
                </p>
              </div>
              <span className="text-sm font-semibold tabular-nums flex-shrink-0">
                {fmtCurrency(item.amount)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Velocity stats — bottom strip */}
      {velocity.paid_invoice_sample_size > 0 && (
        <div className="grid grid-cols-3 gap-2 pt-4 border-t">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Avg days to paid</p>
            <p className="text-base font-bold tabular-nums">{velocity.avg_days_to_paid}d</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">On-time rate</p>
            <p className="text-base font-bold tabular-nums">{velocity.on_time_rate_pct}%</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Paid online</p>
            <p className="text-base font-bold tabular-nums">{velocity.online_pay_rate_pct}%</p>
          </div>
        </div>
      )}
    </div>
  );
}
